/**
 * The purchase sheet's execution, without a screen (LIS-1173, the purchase
 * design's §2.5, §2.6 and §2.8): the buyer's signed-in read of the
 * purchase, their card, the answered start and the attempt that follows,
 * drawn from its view. Hosts supply the transport and draw the result; the
 * rules live here, and none of them names a seller.
 *
 * - A generation guards every answer: after `dispose`, another `load` or
 *   `back`, late answers and streams are dropped.
 * - Where the sheet opened a purchase ahead of Buy (`warming`, spine v4),
 *   Buy authorises it: the start carries its id and the sheet's capability.
 *   Its refusal before Buy is said as a refused start's would be. It is
 *   told when Buy's attempt is over, and going back from an ended attempt
 *   opens the sheet a new one (Erik, 2 Nov 2026).
 * - One stream at a time, for an open attempt only. A read older than the
 *   one shown (a lower sequence), or one of the same step that goes back
 *   (running again after a stop was heard, unanswered after an answer), is
 *   dropped.
 * - A start whose answer is lost is looked for by its own id, then among
 *   the buyer's active attempts, before it counts as failed. Refused by the
 *   server, it is over; unanswered, it is kept (`pendingStart`) and every
 *   retry sends exactly it again, which the server answers idempotently.
 * - The attempt adopted — started, recovered or resumed — is the one
 *   `onAttempt` is told about, so a reload finds it.
 * - Only an ended attempt can be left, and not while a request that could
 *   start or stop one is still unanswered.
 * - The gates run in the design's order: the sale, then the total, then
 *   the payment policy (§2.8). A demo is never started.
 * - A read again (another delivery country) that gives no purchase, or fails, leaves the one drawn as it is
 *   and says the read failed, so the buyer can choose again.
 */
import { answeredRegion, orderTotal, type Answers, type AttemptAnswer, type Purchase, type StartRefusal } from "@libs/platform-wire/purchase";
import type { BuyerIdentity, CardSummary } from "@libs/buyer-identity-client";
import { JourneyError, type ChosenDelivery } from "../contracts";
import { cvcFresh } from "../card-choice";
import { cardTaken } from "./cards";
import { formComplete, nameAnswers } from "./form";
import { attemptOpen, StartRefused, type AttemptRead, type PurchaseStart, type PurchaseTransport } from "./transport";
import type { PurchaseTiming, PurchaseStep } from "./timing";
import type { PurchaseWarming } from "./warming";

export type PurchaseJourneyState = {
  /** The signed-in buyer's read of the purchase; null until read, and
   *  where the access offers none. */
  purchase: Purchase | null;
  /** The delivery country it was read for; null for the read it loaded
   *  with. */
  readFor: string | null;
  identity: BuyerIdentity | null;
  attempt: AttemptRead | null;
  /** A request is in flight: loading, starting, answering or stopping. */
  busy: boolean;
  /** The card and the start are being submitted as one. */
  submitting: boolean;
  /** The one sentence the checkout's notice shows, or nothing. */
  error: string | null;
  /** The server's refusal of the last start, with the field it is about. */
  refusal: StartRefusal | null;
  /** This checkout's start request: kept across retries until the buyer
   *  goes back to the checkout. */
  requestId: string;
  /** A start whose answer never came and whose attempt was not found: every
   *  retry sends exactly it. */
  pendingStart: PurchaseStart | null;
  /** A read for another delivery country must settle before a start. */
  readStatus: "ready" | "loading" | "failed";
};

/** What pays for one submit: the saved card chosen, pinned for the whole
 *  submit, and how to tokenise what the secure fields hold. */
export type SubmitCard = {
  selected: CardSummary | null;
  capture: (() => Promise<CardSummary>) | null;
};

/** The order a submit starts: the purchase's listing, the lines chosen and
 *  the delivery option pinned from the store's estimate of them. */
export type SubmitOrder = {
  lines: { item_id: string; quantity: number }[];
  delivery: (ChosenDelivery & { feeMinor: number }) | null;
};

export type SubmitResult =
  | { ok: true; cardId: string | null }
  | {
      ok: false;
      /** The notice's sentence; empty where the checkout already says it
       *  in place (a card its seller won't take, beside the card). */
      message: string;
      /** The answers don't read: the form shows what's wrong. */
      details?: boolean;
      cardId?: string;
    };

const STEP_FAILED = "Could not complete that step. Please try again.";
const UNCONFIRMED = "We couldn’t confirm your checkout started. Try again to check — nothing will be started twice.";
const CODE_LAPSED = "Your card’s security code has expired. Please enter it again.";
const NO_VIEW = "We couldn’t read your checkout’s progress. It carries on: this will update.";

/** The server answered and said no: nothing was started by this request. */
const refused = (cause: unknown) =>
  cause instanceof JourneyError && cause.code !== "attempt_conflict" && cause.code !== "attempt_already_active";

/** Whether `incoming` is an older read of the attempt shown: a lower step,
 *  or the same step gone back (running again once stopping was heard, a
 *  prompt unanswered once its answer was). */
function stale(shown: AttemptRead, incoming: AttemptRead): boolean {
  const before = shown.view;
  const after = incoming.view;
  if (!before || !after) return false;
  if (after.sequence !== before.sequence) return after.sequence < before.sequence;
  if (before.lifecycle !== "running" && after.lifecycle === "running") return true;
  const answered = (view: typeof before) =>
    view.screen.kind === "ask" || view.screen.kind === "confirm_total" ? view.screen.answered : false;
  return answered(before) && !answered(after) && before.screen.kind === after.screen.kind;
}

/** The step an answer names: its screen's, where it asks. */
const stepOf = (attempt: AttemptRead): number | null => {
  const view = attempt.view;
  if (!view) return null;
  switch (view.screen.kind) {
    case "ask":
    case "confirm_total":
      return view.screen.sequence;
    case "progress":
    case "live_view":
    case "outcome":
      return null;
  }
};

export function createPurchaseJourney(
  transport: PurchaseTransport,
  options: {
    newId: () => string;
    /** The attempt to resume after a reload (or `null`: nothing to). */
    onAttempt?: (id: string | null) => void;
    now?: () => number;
    /** The buyer's view of each press, for metrics. */
    timing?: PurchaseTiming;
    /** The purchase the sheet opened ahead of Buy, where it opened one. */
    warming?: () => PurchaseWarming | null | undefined;
  },
) {
  const now = options.now ?? (() => Date.now());
  const { timing } = options;
  const timed = <T>(step: PurchaseStep, work: () => Promise<T>) => (timing ? timing.measure(step, work) : work());
  let state: PurchaseJourneyState = {
    purchase: null,
    readFor: null,
    identity: null,
    attempt: null,
    busy: false,
    submitting: false,
    error: null,
    refusal: null,
    requestId: options.newId(),
    pendingStart: null,
    readStatus: "ready",
  };
  let accessId: string | null = null;
  let stopWatch: (() => void) | undefined;
  let disposed = false;
  let generation = 0;
  let readGeneration = 0;
  let starting = false;
  let stopping = false;
  let answering = false;
  /** The taxed total accepted for a step whose answer went unanswered: the
   *  server keeps the first answer, so refusing it then stops instead. */
  let acceptSent: { id: string; sequence: number } | null = null;
  /** Whether this load already opened the buyer's seller account ahead of
   *  Pay (`namesEntered`). */
  let accountAsked = false;
  /** The read again that failed, for `retryRead`: for a delivery country,
   *  or the purchase as last read. */
  let failedRead: { deliverTo: string | null } | null = null;
  /** The early refusal already said, so it is said once. */
  let earlySaid: StartRefusal | null = null;
  let unhear: (() => void) | undefined;
  const listeners = new Set<() => void>();
  const set = (patch: Partial<PurchaseJourneyState>) => {
    if (disposed) return;
    state = { ...state, ...patch };
    for (const fn of listeners) fn();
  };
  const unwatch = () => {
    stopWatch?.();
    stopWatch = undefined;
  };
  const failed = (message = STEP_FAILED) => set({ busy: false, error: message });
  const adopt = (attempt: AttemptRead, current: number) => {
    if (disposed || current !== generation) return;
    readGeneration++;
    options.onAttempt?.(attempt.id);
    set({ requestId: attempt.id, pendingStart: null, readStatus: "ready" });
    observe(attempt, current);
  };
  const observe = (incoming: AttemptRead, current: number) => {
    if (disposed || current !== generation) return;
    const shown = state.attempt;
    const same = shown?.id === incoming.id;
    // An older read is dropped; the request it answered (a stop, an answer)
    // is over all the same.
    if (same && stale(shown, incoming)) {
      if (state.busy) set({ busy: false });
      return;
    }
    set({ attempt: incoming, busy: false, error: incoming.view ? null : NO_VIEW });
    if (!attemptOpen(incoming)) options.warming?.()?.over(incoming.id);
    if (same && stopWatch) {
      if (!attemptOpen(incoming)) unwatch();
      return;
    }
    unwatch();
    if (!attemptOpen(incoming)) return;
    stopWatch = transport.watch(
      incoming.id,
      (value) => {
        if (value.id !== incoming.id) return;
        timing?.status(value);
        observe(value, current);
      },
      (terminal) => {
        if (terminal && !disposed && current === generation) set({ error: STEP_FAILED });
      },
    );
  };
  const find = async (id: string | null, event: string) => {
    const byId = id ? await transport.status(id).catch(() => null) : null;
    return byId ?? (await transport.active(event));
  };
  /** The signed-in read again, for the country it was last read for. */
  const reread = async (current: number) => {
    const event = accessId;
    if (!event) return;
    const reading = ++readGeneration;
    set({ readStatus: "loading" });
    const failed = () => {
      failedRead = { deliverTo: null };
      set({ readStatus: "failed" });
    };
    try {
      const purchase = await transport.read(event, state.readFor ? { deliverTo: state.readFor } : undefined);
      if (current !== generation || reading !== readGeneration || disposed) return;
      if (!purchase) return failed();
      failedRead = null;
      set({ purchase, readStatus: "ready" });
    } catch {
      if (current !== generation || reading !== readGeneration || disposed) return;
      failed();
    }
  };
  /** The sheet's purchase ended before Buy, refused by its seller: said
   *  where a refused start is, unless a start is under way or followed. */
  const heard = () => {
    const refusal = options.warming?.()?.snapshot().refusal ?? null;
    if (!refusal || refusal === earlySaid || disposed || state.attempt || starting || state.submitting) return;
    earlySaid = refusal;
    set({ refusal, error: refusal.message });
  };
  /** What the buyer is told of a start the server refused: its own words
   *  where it gave them. */
  const refusalMessage = (cause: unknown) => (cause instanceof StartRefused ? cause.refusal.message : STEP_FAILED);

  const controller = {
    snapshot: () => state,
    subscribe(fn: () => void) {
      listeners.add(fn);
      return () => {
        listeners.delete(fn);
      };
    },
    /** This access's purchase for the signed-in buyer; `id` resumes an
     *  attempt started earlier (a reload). `deliverTo`: the delivery country
     *  the buyer's address named in an earlier checkout of this sheet, whose
     *  read the order was chosen from, read first. */
    async load(event: string, id?: string | null, at: { deliverTo?: string | null } = {}) {
      disposed = false;
      const current = ++generation;
      unwatch();
      unhear?.();
      unhear = options.warming?.()?.subscribe(heard);
      earlySaid = null;
      accountAsked = false;
      accessId = event;
      starting = false;
      stopping = false;
      answering = false;
      set({
        busy: true,
        submitting: false,
        error: null,
        refusal: null,
        attempt: null,
        purchase: null,
        readFor: at.deliverTo ?? null,
        identity: null,
        pendingStart: null,
        requestId: options.newId(),
        readStatus: "ready",
      });
      try {
        const [purchase, identity] = await Promise.all([
          transport.read(event, at.deliverTo ? { deliverTo: at.deliverTo } : undefined),
          transport.identity(),
        ]);
        if (current !== generation) return;
        set({ purchase, identity });
        const attempt = await find(id ?? null, event);
        if (current !== generation) return;
        if (attempt && attemptOpen(attempt)) adopt(attempt, current);
        else if (attempt && id === attempt.id && attempt.view?.payClaimed) {
          // The buyer left after Pay (free to, while it was `confirming`) and
          // it has ended since: its result, once; the next opening is fresh.
          adopt(attempt, current);
          options.onAttempt?.(null);
        } else {
          if (id) options.onAttempt?.(null);
          // Nothing of the buyer's is open for the access.
          options.warming?.()?.over();
          set({ busy: false });
          heard();
        }
      } catch {
        if (current === generation) failed();
      }
    },
    /** Back in front: the attempt as it stands, and its stream again. */
    async refresh() {
      const attempt = state.attempt;
      if (!attempt || disposed) return;
      const current = generation;
      try {
        const value = await transport.status(attempt.id);
        timing?.status(value);
        observe(value, current);
      } catch {
        /* The stream, or the next refresh, brings it. */
      }
    },
    cardSaved(card: CardSummary) {
      if (!state.identity) return;
      set({
        identity: {
          ...state.identity,
          cards: state.identity.cards.some((c) => c.id === card.id)
            ? state.identity.cards.map((c) => (c.id === card.id ? card : c))
            : [...state.identity.cards, card],
        },
      });
    },
    /** Start (or, after an unanswered start, retry exactly that start). */
    async start(input: PurchaseStart) {
      if (disposed || starting || attemptOpen(state.attempt) || state.readStatus !== "ready") return false;
      const request = state.pendingStart ?? input;
      const current = generation;
      starting = true;
      set({ busy: true, error: null, refusal: null });
      try {
        const attempt = await timed("start", () => transport.start(request));
        if (current !== generation) return false;
        timing?.started(attempt);
        if (request.purchase) options.warming?.()?.started();
        adopt(attempt, current);
        return true;
      } catch (cause) {
        // The sheet's purchase is read by its id before Buy too: only the
        // buyer's active attempts (which leave it out until Buy) say Buy
        // reached it.
        const found = await find(request.purchase ? null : request.id, request.accessId).catch(() => null);
        if (current !== generation) return false;
        // Both reads are of this access: the start's own attempt, or the
        // buyer's open one for it (started from another tab), is followed.
        if (found && (found.id === request.id || attemptOpen(found))) {
          timing?.started(found);
          if (request.purchase && found.id === request.id) options.warming?.()?.started();
          adopt(found, current);
          return true;
        }
        if (refused(cause)) {
          set({ pendingStart: null, refusal: cause instanceof StartRefused ? cause.refusal : null });
          options.warming?.()?.over();
          options.onAttempt?.(null);
          // The sheet's purchase is gone, or not this buyer's: the next Buy
          // starts one as today.
          if (request.purchase && cause instanceof JourneyError && cause.code === "attempt_not_found")
            options.warming?.()?.lapse();
          failed(refusalMessage(cause));
          // A refusal about a field is answered on the purchase read again.
          if (cause instanceof StartRefused && cause.refusal.fieldId) reread(current).catch(() => {});
        } else {
          set({ pendingStart: request });
          // An unanswered start may exist server-side: keep its exact id for
          // a reload; only a definite refusal may forget it.
          options.onAttempt?.(request.id);
          failed(UNCONFIRMED);
        }
        return false;
      } finally {
        if (current === generation) starting = false;
      }
    },
    /**
     * Continue: the sale, then the total, then the payment policy (§2.8);
     * the card — the saved one chosen, pinned for the whole submit, its code
     * used while still fresh at that moment, else the secure fields
     * tokenised — then the start with the buyer's answers. A complete total
     * of 0 at a seller that takes free orders asks for no card. After an
     * unanswered start, only that start is retried.
     */
    async submit(input: { answers: Answers; order: SubmitOrder; card: SubmitCard }): Promise<SubmitResult> {
      if (state.submitting || starting || attemptOpen(state.attempt) || disposed || state.readStatus !== "ready")
        return { ok: false, message: "" };
      const current = generation;
      const pending = state.pendingStart;
      if (pending) {
        timing?.pressed(null);
        set({ submitting: true });
        try {
          const started = await controller.start(pending);
          return started ? { ok: true, cardId: pending.cardId } : { ok: false, message: state.error ?? STEP_FAILED };
        } finally {
          if (current === generation) set({ submitting: false });
        }
      }
      const purchase = state.purchase;
      const event = accessId;
      if (!purchase || !event) return { ok: false, message: STEP_FAILED };
      // 1. The sale: a demo is never started.
      switch (purchase.sale.kind) {
        case "demo":
          return { ok: false, message: purchase.sale.message };
        case "buyable":
          break;
      }
      // 2. The total, as the server will quote it.
      const total = orderTotal(
        purchase,
        input.order.lines.map((line) => ({ id: line.item_id, quantity: line.quantity })),
        input.order.delivery,
        answeredRegion(input.answers),
      );
      if (total.kind !== "complete") return { ok: false, message: STEP_FAILED };
      if (!formComplete(purchase.form.sections, input.answers))
        return { ok: false, message: "Please check your details.", details: true };
      // 3. The payment policy: a free order where the seller takes them.
      const free = total.totalMinor === 0;
      if (free && !purchase.payment.freeOrders) return { ok: false, message: STEP_FAILED };
      const { selected, capture } = input.card;
      const start = (cardId: string | null): PurchaseStart => ({
        id: state.requestId,
        accessId: event,
        listingId: purchase.listingId,
        lines: input.order.lines,
        answers: input.answers,
        totalMinor: total.totalMinor,
        // The tax shown on top of it, where the checkout showed one.
        ...(total.taxMinor !== null ? { taxMinor: total.taxMinor } : {}),
        currency: purchase.currency,
        ...(input.order.delivery
          ? {
              delivery: {
                estimateId: input.order.delivery.estimateId,
                sheetId: input.order.delivery.sheetId,
                code: input.order.delivery.code,
              },
            }
          : {}),
        cardId,
      });
      const warming = options.warming?.() ?? null;
      /** The start, carrying the sheet's purchase where it opened one; only
       *  then is it waited for, and a start without one goes at once. */
      const authorised = async (open: PurchaseWarming, cardId: string | null): Promise<PurchaseStart | null> => {
        const warm = await open.authorise(input.order.lines);
        if (current !== generation) return null;
        return { ...start(cardId), ...warm };
      };
      let settled: string | undefined;
      timing?.pressed(free ? "free" : selected ? "saved" : "new");
      set({ submitting: true, error: null, refusal: null });
      try {
        if (free) {
          const request = warming ? await authorised(warming, null) : start(null);
          if (!request) return { ok: false, message: "" };
          const started = await controller.start(request);
          return started ? { ok: true, cardId: null } : { ok: false, message: state.error ?? STEP_FAILED };
        }
        // Read now: a code that lapsed meanwhile is not one the browser can
        // use for its whole run.
        let card = selected && cvcFresh(selected, now()) ? selected : null;
        if (!card) {
          if (!capture) throw Error(selected ? CODE_LAPSED : "Please enter your card details.");
          card = await timed("card", capture);
          if (selected && card.id !== selected.id) throw Error("Could not confirm the selected card. Please try again.");
          if (current === generation) controller.cardSaved(card);
        }
        settled = card.id;
        if (current !== generation) return { ok: false, message: "" };
        // A card the seller's checkout does not take is never started with:
        // the card slot says why beside it.
        if (!cardTaken(purchase.payment, card)) return { ok: false, message: "", cardId: card.id };
        warming?.card(card.id);
        const request = warming ? await authorised(warming, card.id) : start(card.id);
        if (!request) return { ok: false, message: "", cardId: card.id };
        const started = await controller.start(request);
        return started
          ? { ok: true, cardId: card.id }
          : { ok: false, message: state.error ?? STEP_FAILED, cardId: card.id };
      } catch (error) {
        return {
          ok: false,
          cardId: settled,
          message:
            error instanceof Error && error.message && !/^[a-z_]+$/.test(error.message)
              ? error.message
              : "Could not prepare checkout. Please try again.",
        };
      } finally {
        if (current === generation) set({ submitting: false });
      }
    },
    /** Stop the open attempt (again, where the last stop is unconfirmed). */
    async cancel() {
      const attempt = state.attempt;
      if (!attempt || !attemptOpen(attempt) || stopping) return;
      const current = generation;
      stopping = true;
      set({ busy: true, error: null });
      try {
        observe(await transport.cancel(attempt.id), current);
      } catch {
        if (current === generation) failed("Could not stop checkout. Please try again.");
      } finally {
        if (current === generation) stopping = false;
      }
    },
    /**
     * The buyer's answer to what the attempt's screen asks: its fields (a
     * code the seller sent), another way to send the code, or the taxed
     * total. One answer per step: a total refused after its acceptance went
     * unanswered stops the purchase instead, which holds until Pay.
     */
    async answer(answer: AttemptAnswer["answer"]) {
      const attempt = state.attempt;
      const sequence = attempt ? stepOf(attempt) : null;
      if (!attempt || sequence === null || answering) return;
      if (answer.kind === "confirm_total") {
        if (!answer.accept && acceptSent?.id === attempt.id && acceptSent.sequence === sequence) return controller.cancel();
        if (answer.accept) acceptSent = { id: attempt.id, sequence };
      }
      const current = generation;
      answering = true;
      set({ busy: true, error: null });
      try {
        observe(await transport.answer(attempt.id, { sequence, answer }), current);
      } catch {
        if (current !== generation) return;
        switch (answer.kind) {
          case "ask":
            failed("Could not send your code. Please try again.");
            break;
          case "resend":
            failed("Could not ask for another code. Please try again.");
            break;
          case "confirm_total":
            failed(answer.accept ? "Could not confirm your total. Please try again." : "Could not stop checkout. Please try again.");
            break;
        }
      } finally {
        if (current === generation) answering = false;
      }
    },
    /**
     * Where the order goes (the address's country on the form): a purchase
     * whose address reprices it is read again for that country, and its
     * fields, prices and sale follow. Never while an attempt is followed or
     * a start is under way.
     */
    async deliverTo(country: string | null | undefined) {
      const purchase = state.purchase;
      const event = accessId;
      if (!country || !purchase || !event) return;
      if (country === (state.readFor ?? null)) {
        // Back to the country the purchase drawn was read for, after
        // another's read failed: it holds again.
        if (state.readStatus === "failed" && failedRead?.deliverTo !== null) {
          failedRead = null;
          set({ readStatus: "ready" });
        }
        return;
      }
      const reprices = purchase.form.sections.some((section) =>
        section.fields.some((field) => field.kind === "address" && field.reprices),
      );
      if (!reprices) return;
      if (state.attempt || state.submitting || starting || state.pendingStart) return;
      const current = generation;
      const reading = ++readGeneration;
      const before = state.readFor;
      set({ readStatus: "loading", readFor: country });
      // A country no listing serves gives no purchase: the one drawn stands,
      // and the buyer can choose another country, or this one again.
      const failed = () => {
        failedRead = { deliverTo: country };
        set({ readStatus: "failed", readFor: before });
      };
      try {
        const fresh = await transport.read(event, { deliverTo: country });
        if (current !== generation || reading !== readGeneration || disposed) return;
        if (state.attempt || state.submitting || starting || state.pendingStart) return;
        if (fresh) {
          failedRead = null;
          set({ purchase: fresh, readStatus: "ready" });
        } else failed();
      } catch {
        if (current !== generation || reading !== readGeneration || disposed) return;
        failed();
      }
    },
    /** The purchase read again (the seller's prices moved): its fields
     *  arrive as saved now. */
    reread: () => reread(generation),
    /** The read again that failed, asked once more: the delivery country's,
     *  or the purchase as last read. Continue waits for it either way. */
    async retryRead() {
      const failed = failedRead;
      if (!failed || state.readStatus !== "failed") return;
      if (failed.deliverTo !== null) return controller.deliverTo(failed.deliverTo);
      return reread(generation);
    },
    /** Back to the checkout from an ended attempt, with a new request, and
     *  the sheet's purchase opened again where it ended (`renew`). Refused
     *  while anything that could start or stop one is unanswered. */
    back() {
      if (attemptOpen(state.attempt) || starting || stopping || state.submitting || state.pendingStart) return false;
      generation++;
      unwatch();
      options.onAttempt?.(null);
      set({ attempt: null, error: null, refusal: null, busy: false, requestId: options.newId() });
      options.warming?.()?.renew();
      return true;
    },
    /**
     * The buyer's first and last name are answered: their seller account
     * for this purchase is opened from them now, ahead of Pay (`prepare`):
     * once per load, only for a buyer with no agent-mail address every
     * seller takes yet, and never once a start is under way. Fire and
     * forget.
     */
    namesEntered(answers: Answers) {
      const purchase = state.purchase;
      const event = accessId;
      if (
        !purchase ||
        !event ||
        disposed ||
        accountAsked ||
        purchase.sale.kind === "demo" ||
        state.identity?.agentMailAddress !== false ||
        state.submitting ||
        starting ||
        state.attempt !== null ||
        state.pendingStart !== null
      )
        return;
      const names = nameAnswers(purchase, answers);
      if (!names) return;
      accountAsked = true;
      try {
        void Promise.resolve(transport.prepare({ accessId: event, listingId: purchase.listingId, answers: names })).catch(
          () => {},
        );
      } catch {
        /* Best effort: Pay opens the account. */
      }
    },
    dispose() {
      disposed = true;
      generation++;
      unwatch();
      unhear?.();
      unhear = undefined;
    },
  };
  return controller;
}
export type PurchaseJourney = ReturnType<typeof createPurchaseJourney>;
